/* global removeTrapFocus */

// 2026-09-09: responses keyed by full request URL, at MODULE scope so the cache
// outlives any single initQuickSearch() call. Backspacing, or retyping a word you
// just deleted, then costs nothing at all - MEASURED: a repeated query fetches the
// product rows 0 times.
const searchResponseCache = new Map();

// ---------------------------------------------------------------------------
// 2026-09-14: DROPDOWN v3 - "the Amazon layout". Aaron, with two screenshots:
//   "it gives you the information underneath ... it literally just gives you the
//    text. On the app it gives you products as a square scrollable banner at the
//    top. The text is the optimal thing; the imagery is secondary."
//   "ours goes behind the keyboard, so it cuts off half of the stuff."
//   "the LeMieux boots with no sales are still showing first."
//
// So, top to bottom:
//   1. a horizontal STRIP of the top products (square photo, name, price)
//   2. TEXT rows: the query itself ("see all N results"), then refinements built
//      from what the results actually contain - "horse riding boots LADIES",
//      "... CHILDS", "... WATERPROOF", "... LeMieux" - plus Shopify's own
//      suggested phrases when it has any. Tap = the results page for that phrase,
//      exactly as if it had been typed and searched.
//   3. the full compact LIST, which keeps loading as they scroll.
//
// WHERE THE PRODUCTS COME FROM changed too. Shopify's instant endpoint hands over
// at most 8 candidates chosen by TEXT match, and for "horse riding boots" 7 of the
// 8 had never sold (the LeMieux titles literally contain "horse riding"). The
// ranking can only re-order what it is given. So the first request now goes to the
// FULL search engine (sections/search-more.liquid, 24 per page, already ranked by
// orders - see snippets/search-rank.liquid) with options[prefix]=last so a
// half-typed last word still matches. MEASURED: same speed when warm (0.16s vs
// 0.18s), and the sellers appear - Vinci 13 orders, two Brogini, Rhinegold - with
// the unsold LeMieux dropping to 9th and below.
//
// THE KEYBOARD: the panel is position:fixed and used to run the full screen height,
// so the phone keyboard covered its bottom half. Now it sizes itself to
// window.visualViewport (the part of the screen the keyboard is NOT covering) and
// scrolls inside that; and the first scroll of the results drops the keyboard, so
// the whole panel comes back. That is what Amazon does.
// ---------------------------------------------------------------------------

const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    // Clicking close button
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.remove('show-search');
        this.style.maxHeight = '';
        this.style.top = '';
        removeTrapFocus(theme.trapOpener);
      });
    });

    // Pressing escape key
    this.querySelector('.main-search__input').addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape') {
        this.querySelector('.main-search__close').dispatchEvent(new Event('click'));
      }
    });

    // The X in the box (2026-09-15): clears what was typed (the panel goes back
    // to trending / recent); on a phone, with nothing typed, it closes the search
    // instead. Wired here, not in initQuickSearch, because the markup and the CSS
    // show it whether or not quick search is switched on. Its spoken label
    // follows what a tap will do.
    {
      const input = this.querySelector('.main-search__input');
      const clearBtn = this.querySelector('.main-search__clear');
      const syncTyped = () => {
        const typed = input.value.length > 0;
        this.classList.toggle('main-search--typed', typed);
        if (clearBtn) clearBtn.setAttribute('aria-label', typed ? (clearBtn.dataset.labelClear || 'Clear search') : (clearBtn.dataset.labelClose || 'Close'));
      };
      input.addEventListener('input', syncTyped);
      input.addEventListener('change', syncTyped);
      syncTyped();
      this.syncTyped = syncTyped;
      if (clearBtn) {
        clearBtn.addEventListener('click', (evt) => {
          evt.preventDefault();
          if (input.value.length) {
            input.value = '';
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.focus();
          } else {
            const close = this.querySelector('.main-search__close');
            if (close) close.dispatchEvent(new Event('click'));
          }
        });
      }
    }

    // Search as you type
    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }

    // Result navigation
    this.activeResultIndex = -1;
    this.querySelector('.main-search__input').addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  handleKeyDown(evt) {
    const options = [...this.querySelectorAll('.main-search__results [role="option"]')];

    // 2026-09-09: with no results, ArrowDown gave Math.min(0 + 1, -1) = -1, so
    // options[-1] was undefined and .classList threw a TypeError. Keyboard users hit
    // that on every no-results search.
    if ((evt.key === 'ArrowDown' || evt.key === 'ArrowUp') && options.length === 0) return;

    const updateActiveOption = () => {
      options.forEach((opt) => opt.classList.remove('search-result-active'));
      const active = options[this.activeResultIndex];
      if (!active) {
        evt.currentTarget.removeAttribute('aria-activedescendant');
        return;
      }
      active.classList.add('search-result-active');
      evt.currentTarget.setAttribute('aria-activedescendant', active.id);
      try { active.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (e) { /* old browsers */ }
    };

    if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      this.activeResultIndex = Math.min(this.activeResultIndex + 1, options.length - 1);
      updateActiveOption();
    }

    if (evt.key === 'ArrowUp') {
      evt.preventDefault();
      this.activeResultIndex = Math.max(this.activeResultIndex - 1, 0);
      updateActiveOption();
    }

    if (evt.key === 'Enter' && this.activeResultIndex >= 0) {
      // Only swallow Enter if a link is genuinely there. Otherwise let the form
      // submit and take the shopper to the full results page, which is what they
      // expect - previously this threw if the highlighted row had no anchor.
      const activeRow = options[this.activeResultIndex];
      if (activeRow && activeRow.matches('a[href]')) {               // a text row is itself the link
        evt.preventDefault();
        activeRow.click();
        return;
      }
      const activeLink = activeRow ? activeRow.querySelector('a') : null;
      if (activeLink) {
        evt.preventDefault();
        activeLink.click();
      }
    }
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    const resultsBox = this.querySelector('.main-search__results');
    // 2026-09-09: was 500ms. Half a second of nothing after you stop typing, BEFORE
    // the request is even sent, is most of why this felt slow. 120ms is about the
    // shortest delay that still avoids firing on every keystroke.
    const searchTimeoutThrottle = 160;
    const STRIP_MAX = 10;          // squares in the strip (buildStrip - no longer used by the panel, 2026-09-16)
    const TRENDING_ROWS = 6;       // Trending this week rows while the box is empty
    const COMPLETIONS_MAX = 6;     // text rows under the query row
    // 2026-09-15: pages fetched TOGETHER and sorted as one. Shopify hands over 48
    // text-matched candidates per page and has no sales sort for search, so the
    // sort was within a page and a never-sold listing on page 1 sat above a seller
    // on page 2 (Aaron: "not doing it based on sales - the LeMieux ones are still
    // arriving"). Three pages = 144 candidates sorted by the search-rank key each
    // row carries (data-key). Load-more carries on from page 4.
    const MERGE_PAGES = 3;
    // Rows of several pages in ONE order: by key (pins "!|0" and boosts "!|1"
    // first, then matching words, stock, boosted maker, photo, orders), ties by
    // page then position. The key's LAST segment is the row's index within its
    // own page, so it is dropped before comparing - otherwise page 2's first row
    // would beat page 1's seventh on every tie. A product already seen on an
    // earlier page is skipped.
    const keyBase = (k) => { const p = String(k).lastIndexOf('|'); return p > 0 ? String(k).slice(0, p) : String(k); };
    const mergeRows = (docs) => {
      const seenH = new Set();
      const entries = [];
      docs.forEach((doc, page) => {
        if (!doc) return;
        const more = doc.querySelector('.search-more');
        if (!more) return;
        Array.from(more.querySelectorAll('.product-block')).forEach((row, i) => {
          const a = row.querySelector('a[href*="/products/"]');
          const h = a ? (a.getAttribute('href').split('/products/')[1] || '').split('?')[0] : '';
          if (h) { if (seenH.has(h)) return; seenH.add(h); }
          entries.push({ row, key: row.getAttribute('data-key') || '~', page, i });
        });
      });
      entries.sort((x, y) => {
        const kx = keyBase(x.key);
        const ky = keyBase(y.key);
        return kx < ky ? -1 : kx > ky ? 1 : (x.page - y.page) || (x.i - y.i);
      });
      return entries.map((e) => e.row);
    };
    let searchTimeoutID = -1;
    let searchAbortController = null;

    // ONE builder for the results request, used by the real search, the prefetch and
    // the load-more, so cache keys are byte-identical and every page of a query is
    // fetched with the same options (a page 2 fetched with different options would
    // not be page 2 of the same list).
    // SENTENCE SEARCH (V12, 2026-09-16): "navy childs riding hat 54cm" searches
    // "riding hat" with Navy / Children / 52-54cm as filters (search-understand.js).
    const understand = (q) => {
      const r = window.jhrUnderstand ? window.jhrUnderstand.parse(q) : null;
      return r && r.changed ? r : null;
    };
    // (2026-09-17, tried and REVERTED the same morning: dropping "horse" from
    // "horse riding boots" before Shopify. Shopify wants every word of a two-word
    // search, so "riding boots" lost every boot whose listing lacks the word
    // "riding" - Premier Equine Vinci, the best seller, first among them. The
    // three-word search, "any two of the three", is the wider net.)
    const applyUnderstanding = (u, q) => {
      const r = understand(q);
      u.searchParams.set('q', r ? r.q : q);
      if (r) {
        Object.keys(r.filters).forEach((p) => { u.searchParams.delete(p); r.filters[p].forEach((v) => u.searchParams.append(p, v)); });
        u.searchParams.set('nl', q);                       // the sentence as typed
      }
      return u;
    };
    const buildSearchUrl = (q, page) => {
      const u = new URL(theme.routes.base + '/search', location.origin);
      applyUnderstanding(u, q);
      u.searchParams.set('type', 'product');
      u.searchParams.set('options[prefix]', 'last');
      u.searchParams.set('section_id', 'search-more');
      u.searchParams.set('page', String(page || 1));
      return u;
    };
    const buildSuggestUrl = (q) => {
      const u = new URL(theme.routes.base + '/search/suggest.json', location.origin);
      u.searchParams.set('q', q);
      u.searchParams.set('resources[type]', 'query');
      u.searchParams.set('resources[limit]', '6');
      return u;
    };
    // In-flight requests are shared: when the prefetch for "boots" is still on the
    // wire and the shopper finishes typing "boots", the real search joins that
    // request instead of firing an identical one.
    const pending = new Map();
    const cachedText = (url, init) => {
      const key = url.toString();
      const hit = searchResponseCache.get(key);
      if (hit !== undefined) return Promise.resolve(hit);
      if (pending.has(key)) return pending.get(key);
      const p = fetch(url, init)
        .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
        .then((text) => {
          // A shopper will not type hundreds of distinct queries in one visit;
          // the cap is only so a scripted hammering cannot grow this forever.
          // Three pages per query since 2026-09-15, hence 180 (was 60).
          if (searchResponseCache.size > 180) searchResponseCache.clear();
          searchResponseCache.set(key, text);
          return text;
        })
        .finally(() => { pending.delete(key); });
      pending.set(key, p);
      return p;
    };

    // 2026-09-11: PREFETCH. Shopify's search backend forgets a query in about a
    // minute and the CDN never caches it, so scheduled warming cannot work. What
    // does work: the moment suggestions say the shopper is probably typing "boots",
    // fetch "boots" into the cache. When they finish typing, the real search is a
    // memory hit - 0ms. Never touches the live request's abort controller.
    let prefetchInFlight = '';
    window.jhrSearchPrefetch = (q) => {
      q = (q || '').trim();
      if (q.length < 2 || q === searchInput.value.trim() || q === prefetchInFlight) return;
      // all the pages the real search will want, or the warm-up buys nothing
      const urls = Array.from({ length: MERGE_PAGES }, (_, i) => buildSearchUrl(q, i + 1))
        .filter((u) => !searchResponseCache.has(u.toString()));
      if (!urls.length) return;
      prefetchInFlight = q;
      Promise.all(urls.map((u) => cachedText(u, { method: 'get', priority: 'low' }).catch(() => '')))
        .finally(() => { if (prefetchInFlight === q) prefetchInFlight = ''; });
    };

    // -----------------------------------------------------------------------
    // Keyboard fit. visualViewport is the part of the screen the keyboard is not
    // covering; the panel is fixed to the LAYOUT viewport, so without this it runs
    // on underneath the keys. Applied while the search is open; cleared on close.
    // -----------------------------------------------------------------------
    const vv = window.visualViewport;
    const fitToViewport = () => {
      if (!vv || !document.body.classList.contains('show-search')) return;
      this.style.maxHeight = `${Math.round(vv.height)}px`;
      this.style.top = `${Math.round(vv.offsetTop)}px`;
    };
    if (vv) {
      vv.addEventListener('resize', fitToViewport);
      vv.addEventListener('scroll', fitToViewport);
    }
    // First scroll of the results drops the keyboard so the whole panel is visible.
    // 2026-09-16 (Aaron: "I had to tap it again a bit harder"): only a REAL
    // scroll counts - 12px or more of vertical movement. A light tap wobbles a
    // few pixels, which used to drop the keyboard, resize the panel under the
    // finger, and lose the tap.
    const dropKeyboard = () => { if (document.activeElement === searchInput) searchInput.blur(); };
    let touchY0 = null;
    this.addEventListener('touchstart', (evt) => { touchY0 = evt.touches && evt.touches[0] ? evt.touches[0].clientY : null; }, { passive: true });
    this.addEventListener('touchmove', (evt) => {
      if (!resultsBox.contains(evt.target) || touchY0 === null || !evt.touches || !evt.touches[0]) return;
      if (Math.abs(evt.touches[0].clientY - touchY0) >= 12) dropKeyboard();
    }, { passive: true });
    // 2026-09-15 (Aaron, on his phone): "when I'm typing, all of the products are
    // hiding behind the keyboard" - once the shopper has clearly finished, the
    // keyboard goes away by itself. "Finished" = the results for what they typed
    // are on screen AND nothing typed for KEYBOARD_REST_MS. Half a second would
    // catch slow typists between letters (a pause between two thumbs is often
    // longer than that); 1.2s does not. Touch screens only - a laptop has no
    // keyboard to hide and losing focus there would just be odd. A tap on the
    // box brings the keyboard straight back.
    // 2026-09-23 (Aaron: his wife, a slow typist, kept losing the keyboard mid-word):
    // 1.2s -> 2.5s. Waiting longer costs little - the results already sit above the
    // keyboard, and the first real scroll drops it at once.
    const KEYBOARD_REST_MS = 2500;
    const touchScreen = () => window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    let lastTypedAt = 0;
    let restTimer = null;
    let resultsFor = '';                                   // query whose results are on screen
    const armKeyboardRest = () => {
      if (restTimer) { clearTimeout(restTimer); restTimer = null; }
      if (!touchScreen() || document.activeElement !== searchInput) return;
      const q = searchInput.value.trim();
      if (q.length < 2 || resultsFor !== q) return;         // results not up yet - keep the keys
      const wait = Math.max(0, KEYBOARD_REST_MS - (Date.now() - lastTypedAt));
      restTimer = setTimeout(() => {
        restTimer = null;
        if (searchInput.value.trim() === resultsFor && document.activeElement === searchInput) searchInput.blur();
      }, wait);
    };
    searchInput.addEventListener('input', () => { lastTypedAt = Date.now(); armKeyboardRest(); });
    this.noteResultsFor = (q) => { resultsFor = String(q || '').trim(); armKeyboardRest(); };

    // -----------------------------------------------------------------------
    // YOUR RECENT SEARCHES - per shopper, in their own browser only. Aaron: "if I
    // log in and search 'horse riding boots joppers', then come back, it will show
    // me those; another user sees theirs." Saved when they ACT on a search (tap a
    // result, tap a refinement, press search, land on the results page) - never on
    // a half-typed word. Never sent to us; nothing to do with Popular searches.
    // -----------------------------------------------------------------------
    const escapeHtmlAttr = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const RECENT_KEY = 'jhr_recent_searches';
    const RECENT_MAX = 6;          // Aaron, 15 Sept: "only remember the last 6"
    const readRecent = () => { try { const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); return Array.isArray(v) ? v.filter((x) => typeof x === 'string').slice(0, RECENT_MAX) : []; } catch (e) { return []; } };
    const saveRecent = (q) => {
      q = (q || '').trim().replace(/\s+/g, ' ');
      if (q.length < 2 || q.length > 60) return;
      try {
        const list = [q, ...readRecent().filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, RECENT_MAX);
        localStorage.setItem(RECENT_KEY, JSON.stringify(list));
      } catch (e) { /* private mode - fine */ }
    };
    const clearRecent = () => { try { localStorage.removeItem(RECENT_KEY); } catch (e) { /* fine */ } };
    const removeRecent = (q) => {
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(readRecent().filter((x) => x !== q))); } catch (e) { /* fine */ }
    };
    const CLOCK = '<svg class="search-completions__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path></svg>';
    // Full-width rows like Amazon's: clock, the words, an x to forget that one.
    const renderRecent = () => {
      const list = readRecent();
      if (!list.length) return null;
      const wrap = document.createElement('div');
      wrap.className = 'search-completions search-recent';
      wrap.setAttribute('role', 'group');
      wrap.setAttribute('aria-label', 'Your recent searches');
      const head = document.createElement('div');
      head.className = 'search-completions__heading';
      head.innerHTML = '<span>Your recent searches</span>';
      const clear = document.createElement('button');
      clear.type = 'button'; clear.className = 'search-recent__clear'; clear.textContent = 'Clear all';
      clear.addEventListener('click', () => { clearRecent(); wrap.remove(); });
      head.appendChild(clear);
      wrap.appendChild(head);
      list.forEach((q) => {
        const row = document.createElement('div');
        row.className = 'search-completions__row search-completions__row--recent';
        const a = document.createElement('a');
        a.className = 'search-completions__link';
        const u = new URL(theme.routes.base + '/search', location.origin); u.searchParams.set('q', q);
        a.href = u.toString();
        a.innerHTML = CLOCK + '<span class="search-completions__text"></span>';
        a.querySelector('.search-completions__text').textContent = q;
        const x = document.createElement('button');
        x.type = 'button'; x.className = 'search-recent__remove';
        x.setAttribute('aria-label', 'Forget "' + q + '"');
        x.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>';
        x.addEventListener('click', (evt) => { evt.preventDefault(); removeRecent(q); row.remove(); if (!readRecent().length) wrap.remove(); });
        row.appendChild(a); row.appendChild(x);
        wrap.appendChild(row);
      });
      return wrap;
    };
    // landing on the results page counts as a search they meant
    try {
      if (/\/search\/?$/.test(location.pathname)) {
        const sp = new URLSearchParams(location.search);
        const q0 = sp.get('q');
        let leaving = false;
        // SPELLING (2026-09-15): "horse riiding boot" gave a page of hoof boots.
        // The results Shopify returned already carry the right word, so ask
        // search-spell.js for a correction from the card titles and go there.
        // `orig` on the corrected page carries the words as typed, for the note
        // below and so the page cannot be corrected twice.
        const orig = sp.get('orig');
        const nl = sp.get('nl');                             // sentence search: the words as typed
        const nlx = sp.get('nlx');                           // ...and the fallback to them
        const hasFilters = Array.from(sp.keys()).some((k) => k.indexOf('filter.') === 0);
        // a plain landing (typed URL, Google, an old link) gets the sentence treatment
        if (q0 && !nl && !nlx && !orig && !hasFilters && !sp.get('page') && understand(q0)) {
          leaving = true;
          location.replace(applyUnderstanding(new URL(location.href), q0).toString());
        }
        // the filters found nothing: back to the exact words, once
        if (q0 && nl && !leaving) {
          const grid0 = document.querySelector('.collection-listing .product-grid');
          if (!grid0 || !grid0.querySelector('.product-block')) {
            const u = new URL(location.href);
            Array.from(u.searchParams.keys()).filter((k) => k.indexOf('filter.') === 0).forEach((k) => u.searchParams.delete(k));
            u.searchParams.set('q', nl);
            u.searchParams.delete('nl');
            u.searchParams.set('nlx', '1');
            leaving = true;
            location.replace(u.toString());
          }
        }
        if (q0 && !leaving && !orig && !sp.get('page') && window.jhrSpell) {
          const titles = Array.from(document.querySelectorAll('.collection-listing .product-grid .product-block__title')).map((el) => el.textContent || '');
          const fixed = window.jhrSpell.correct(q0, titles);
          if (fixed && fixed.toLowerCase() !== q0.toLowerCase()) {
            const u = new URL(location.href);
            u.searchParams.set('q', fixed);
            u.searchParams.set('orig', q0);
            leaving = true;
            location.replace(u.toString());
          }
        }
        // RESCUE (2026-09-29, Aaron): nothing found, or nothing carrying a typed word - a size written the shop's
        // way ("6ft3" -> 6'3), then a brand we sell ("dobson and horrell" -> Dodson & Horrell). Same "Showing
        // results for" line as a spelling fix; never while the shopper's own filters are on.
        if (q0 && !leaving && !orig && !nl && !hasFilters && !sp.get('page') && window.jhrSpell && window.jhrSpell.rescue) {
          const titlesR = Array.from(document.querySelectorAll('.collection-listing .product-grid .product-block__title')).map((el) => el.textContent || '');
          if (!titlesR.length || window.jhrSpell.unrelated(q0, titlesR)) {
            window.jhrSpell.rescue(q0).then((better) => {
              if (!better || better.toLowerCase() === q0.toLowerCase()) return;
              const u = new URL(location.href);
              u.searchParams.set('q', better);
              u.searchParams.set('orig', q0);
              location.replace(u.toString());
            }).catch(() => undefined);
          }
        }
        if (q0 && !leaving) saveRecent(nl || q0);           // the corrected words, not the typo; the sentence, not its parts
        // log clicks on this page (and the search itself if they came from outside)
        if (q0 && !leaving && window.jhrSearchLog && window.jhrSearchLog.onResultsPage) {
          const grid = document.querySelector('.collection-listing .product-grid');
          const shown = grid ? grid.querySelectorAll(':scope > .product-block').length : 0;
          const totalEl = document.querySelector('[data-search-total]');
          const total = totalEl ? parseInt(totalEl.getAttribute('data-search-total'), 10) : -1;
          let fromOutside = true;
          try { fromOutside = !document.referrer || new URL(document.referrer).host !== location.host; } catch (e) { /* treat as outside */ }
          window.jhrSearchLog.onResultsPage(nl || q0, shown, isNaN(total) ? -1 : total, fromOutside, orig && orig.toLowerCase() !== q0.toLowerCase() ? orig : null);
        }
        // "Showing riding hat - Navy, Children, 52-54cm. Search instead for the exact words"
        if (q0 && !leaving && (nl || nlx)) {
          const bar = document.querySelector('.section-search-template .utility-bar');
          if (bar) {
            const note = document.createElement('div');
            note.className = 'search-spell-note search-nl-note';
            if (nl) {
              const chips = Array.from(document.querySelectorAll('.filter-group--applied .filter-group__applied-item'))
                .map((a) => ({ text: (a.querySelector('.filter-group__applied-item__text') || a).textContent.replace(/\s+/g, ' ').trim(), href: a.getAttribute('href') }))
                .filter((c) => c.text);
              const back = new URL(location.href);
              Array.from(back.searchParams.keys()).filter((k) => k.indexOf('filter.') === 0).forEach((k) => back.searchParams.delete(k));
              back.searchParams.set('q', nl);
              back.searchParams.delete('nl');
              back.searchParams.set('nlx', '1');
              // "Show all riding hats": the words without any of the filters (Aaron)
              const all = new URL(location.href);
              Array.from(all.searchParams.keys()).filter((k) => k.indexOf('filter.') === 0).forEach((k) => all.searchParams.delete(k));
              all.searchParams.delete('nl');
              all.searchParams.set('nlx', '1');
              note.innerHTML = 'Showing <b></b><span class="search-nl-note__chips"></span> <a class="search-nl-note__all" href="' + escapeHtmlAttr(all.toString()) + '">Show all <span></span></a> <a class="search-nl-note__exact" href="' + escapeHtmlAttr(back.toString()) + '">Search the exact words instead</a>';
              note.querySelector('b').textContent = q0;
              note.querySelector('.search-nl-note__all span').textContent = q0 + (/s$/i.test(q0) ? '' : 's');
              const wrap = note.querySelector('.search-nl-note__chips');
              chips.forEach((c) => {
                const chip = document.createElement('a');
                chip.className = 'search-nl-note__chip';
                chip.href = c.href || '#';
                chip.textContent = c.text;
                chip.title = 'Remove ' + c.text;
                wrap.appendChild(chip);
              });
              // a thin result: say what the plain words would give (one section fetch)
              const gridN = document.querySelectorAll('.collection-listing .product-grid .product-block').length;
              if (gridN <= 3) {
                const plain = new URL(location.href);
                Array.from(plain.searchParams.keys()).filter((k) => k.indexOf('filter.') === 0 || k === 'nl' || k === 'page').forEach((k) => plain.searchParams.delete(k));
                plain.searchParams.set('q', nl);
                const sectionEl = document.querySelector('.section-search-template') && document.querySelector('.section-search-template').closest('[id^="shopify-section-"]');
                const sid = sectionEl ? sectionEl.id.replace(/^shopify-section-/, '') : '';
                if (sid) {
                  plain.searchParams.set('section_id', sid);
                  fetch(plain.toString()).then((r) => (r.ok ? r.text() : '')).then((html) => {
                    const doc = new DOMParser().parseFromString(html, 'text/html');
                    const n = doc.querySelectorAll('.product-grid > .product-block').length;
                    const more = !!doc.querySelector('.pagination__next');
                    if (n >= gridN + 3) {
                      const line = document.createElement('div');
                      line.className = 'search-nl-note__thin';
                      line.innerHTML = 'Only <b></b> match everything. <a href="' + escapeHtmlAttr(back.toString()) + '">See all <span></span> for "<i></i>"</a>';
                      line.querySelector('b').textContent = String(gridN);
                      line.querySelector('span').textContent = n + (more ? '+' : '');
                      line.querySelector('i').textContent = nl;
                      const exact = note.querySelector('.search-nl-note__exact');
                      if (exact) exact.hidden = true;                 // this line links to the exact words already
                      note.appendChild(line);
                    }
                  }).catch(() => undefined);
                }
              }
            } else {
              note.innerHTML = 'No exact match for the details in <b></b> - showing everything for those words.';
              note.querySelector('b').textContent = q0;
            }
            bar.insertAdjacentElement('beforebegin', note);
          }
        }
        if (q0 && orig && orig.toLowerCase() !== q0.toLowerCase()) {
          const bar = document.querySelector('.section-search-template .utility-bar');
          if (bar) {
            const note = document.createElement('div');
            note.className = 'search-spell-note';
            const back = new URL(location.href);
            back.searchParams.set('q', orig);
            back.searchParams.set('orig', orig);
            note.innerHTML = 'Showing results for <b></b>. <a href="' + escapeHtmlAttr(back.toString()) + '">Search instead for "<span></span>"</a>';
            note.querySelector('b').textContent = q0;
            note.querySelector('span').textContent = orig;
            bar.insertAdjacentElement('beforebegin', note);
          }
        }
      }
    } catch (e) { /* fine */ }
    // pressing the search button / Enter - the sentence goes through the
    // understanding too, keeping whatever hidden fields the form carries
    const form0 = searchInput.closest('form');
    if (form0) form0.addEventListener('submit', (evt) => {
      const typed = searchInput.value;
      saveRecent(typed);
      if (!understand(typed)) return;
      const u = new URL(form0.action, location.origin);
      new URLSearchParams(new FormData(form0)).forEach((value, key) => { u.searchParams.set(key, value); });
      applyUnderstanding(u, typed);
      evt.preventDefault();
      location.href = u.toString();
    });

    // -----------------------------------------------------------------------
    // "Trending this week" while the box is empty. Rendered by
    // sections/search-trending.liquid, fetched once per page view, injected straight
    // in - it is not a search, so it never touches the search logger.
    // -----------------------------------------------------------------------
    let trendingHTML = null;
    let trendingInFlight = null;
    const showTrending = () => {
      if (searchInput.value.length) return;          // they started typing - stand down
      const inject = (html) => {
        if (searchInput.value.length) return;        // typed while we were fetching
        const tpl = document.createElement('template');
        tpl.innerHTML = html;
        const panel = tpl.content.querySelector('.search-trending');
        if (!panel) return;
        resultsBox.innerHTML = '';
        // 2026-09-16 (Aaron): YOUR recent searches first, then Trending this
        // week as ordinary rows (six of them), then Popular searches when it is
        // switched on. The sideways strip is gone - on his phone the strip hid
        // the recent searches under the keyboard, and people scroll down, not
        // across (the log agrees: clicks on squares 1-3, none on 4-5).
        const list = panel.querySelector('.search-list');
        if (list) {
          Array.from(list.querySelectorAll('.product-block')).forEach((row, i) => {
            if (i >= TRENDING_ROWS) { row.remove(); return; }
            row.classList.remove('main-search-result');
          });
        }
        const recent = renderRecent();
        if (recent) panel.insertAdjacentElement('afterbegin', recent);
        resultsBox.appendChild(panel);
        this.classList.add('main-search--has-results', 'main-search--trending');
      };
      if (trendingHTML) { inject(trendingHTML); return; }
      if (trendingInFlight) return;
      trendingInFlight = fetch('/?section_id=search-trending')
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => { trendingHTML = html; trendingInFlight = null; inject(html); })
        .catch(() => { trendingInFlight = null; });  // no trending is not an error worth a shopper seeing
    };

    // -----------------------------------------------------------------------
    // Refinements: which words would narrow THIS search? Built from the titles and
    // makers of the results on screen, so every suggestion is guaranteed to find
    // something. Attribute words first (kids, ladies, long, waterproof, leather...),
    // then makers as whole names ("Premier Equine", never "premier"), then any
    // other word that at least three results share. Nothing here is a dictionary
    // of horse words: the catalogue supplies them. Once the search log has real
    // data, what shoppers actually typed after this phrase takes priority.
    // -----------------------------------------------------------------------
    const STOP = new Set(('the and for with from of in on to a an by one size sizes colour color colours pack set new style ' +
      'cm mm kg ml lt ltr per all plus pro ii iii edition range collection only each pair pairs mixed assorted').split(' '));
    const ATTR = ('kids childs children child junior youth ladies womens women mens men unisex adult adults ' +
      'long tall short waterproof leather synthetic rubber suede wide slim regular standard zip laced lace elastic ' +
      'winter summer lightweight mediumweight heavyweight combo fleece cotton mesh detachable ' +
      'black brown navy pink blue white grey red green purple burgundy').split(' ');
    const ATTR_RANK = new Map(ATTR.map((w, i) => [w, i]));
    const stem = (w) => w.replace(/['\u2019]s?$/, '').replace(/ies$/, 'y').replace(/(ches|shes|xes|sses|zes)$/, (m) => m.slice(0, -2)).replace(/s$/, '');

    const buildRefinements = (q, rows) => {
      const qWords = q.toLowerCase().split(/\s+/).filter(Boolean);
      const qStems = new Set(qWords.map(stem));
      const df = new Map();
      const vendorDf = new Map();
      // 2026-09-16 (Aaron): when the words already name a maker ("child riding
      // hat shires"), a maker suggestion must REPLACE it, not pile on ("child
      // riding hat shires Champion"). Collect the typed words that belong to a
      // maker on this page; they are dropped from the phrase for maker rows.
      const typedMakerWords = new Set();
      rows.forEach((row) => {
        const titleEl = row.querySelector('.search-row__title');
        const title = (titleEl ? titleEl.textContent : '').toLowerCase();
        const vendor = (row.dataset.vendor || '').trim();
        const vendorWords = new Set(vendor.toLowerCase().split(/\s+/));
        const tokens = new Set(title.replace(/[^a-z0-9' ]+/g, ' ').split(/\s+/)
          .filter((t) => t.length >= 3 && !/^\d+$/.test(t) && !STOP.has(t) && !qStems.has(stem(t)) && !vendorWords.has(t)));
        tokens.forEach((t) => df.set(t, (df.get(t) || 0) + 1));
        // skip the maker when the shopper already typed any distinctive part of its
        // name ("premier boots" must not offer "Premier Equine"; "equestrian boots"
        // may still offer "Hy Equestrian")
        const vendorKey = [...vendorWords].filter((w) => w && !['equestrian', 'equine', 'horse', 'uk', 'ltd', 'co', 'the'].includes(w));
        if (vendor && vendorKey.some((w) => qWords.includes(w))) {
          vendorWords.forEach((w) => { if (qWords.includes(w)) typedMakerWords.add(w); });
        } else if (vendor) {
          vendorDf.set(vendor, (vendorDf.get(vendor) || 0) + 1);
        }
      });
      // the phrase a maker row builds on: the words with the typed maker taken out
      const baseSansMaker = typedMakerWords.size
        ? q.split(/\s+/).filter((w) => w && !typedMakerWords.has(w.toLowerCase())).join(' ')
        : q;
      const attrHits = ATTR.filter((w) => (df.get(w) || 0) >= 2)
        .sort((a, b) => (df.get(b) - df.get(a)) || (ATTR_RANK.get(a) - ATTR_RANK.get(b)));
      const vendors = [...vendorDf.entries()].filter(([, n]) => n >= 2).sort((a, b) => b[1] - a[1]).map(([v]) => v);
      const others = [...df.entries()].filter(([w, n]) => n >= 3 && !ATTR_RANK.has(w)).sort((a, b) => b[1] - a[1]).map(([w]) => w);
      const out = [];
      const seen = new Set();
      const vendorSet = new Set(vendors);
      [...attrHits, ...vendors, ...others].forEach((w) => {
        const k = w.toLowerCase();
        if (seen.has(k) || out.length >= COMPLETIONS_MAX) return;
        seen.add(k);
        out.push(vendorSet.has(w) ? `${baseSansMaker} ${w}` : `${q} ${w}`);   // whole phrases
      });
      return out;
    };

    // The last word may be half-typed ("horse riding boo"). options[prefix]=last still
    // finds the boots, but a refinement must never read "horse riding boo ladies".
    // If the last word is not a whole word in any title, replace it with the most
    // common title word that starts with it ("boo" -> "boots"); if none, keep it.
    const completeLastWord = (q, rows) => {
      const trimmed = q.trim();
      if (!trimmed || /\s$/.test(q)) return trimmed;
      const words = trimmed.toLowerCase().split(/\s+/);
      const last = words[words.length - 1];
      if (last.length < 2) return trimmed;
      const counts = new Map();
      let whole = false;
      rows.forEach((row) => {
        const titleEl = row.querySelector('.search-row__title');
        const toks = (titleEl ? titleEl.textContent : '').toLowerCase().replace(/[^a-z0-9' ]+/g, ' ').split(/\s+/).filter(Boolean);
        if (toks.includes(last)) whole = true;
        new Set(toks.filter((t) => t.length > last.length && t.startsWith(last))).forEach((t) => counts.set(t, (counts.get(t) || 0) + 1));
      });
      if (whole || !counts.size) return trimmed;
      const best = [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
      return trimmed.slice(0, trimmed.length - last.length) + best;
    };

    const ICON = '<svg class="search-completions__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="7"></circle><path d="M20 20l-3.5-3.5"></path></svg>';
    const escapeHtml = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

    // Text rows. Row 1 is the query itself and goes to the full results page; the
    // rest fill the box and search again in place. Our refinements come first -
    // every one is a word the results on screen actually contain. Shopify's own
    // phrase suggestions fill any remaining rows, but only when they ADD a real word:
    // MEASURED for "turnout rug" Shopify offered "g turnout rug", "turnout combo rug"
    // and "turnout rug combo" - fragments and reorderings, not refinements.
    // q = as typed (row 1); base = with the last word completed (refinement rows).
    // Emphasis is ONE rule for every row: the words the shopper did not type are
    // bold. (Shopify's styled_text bolds the opposite - the typed part - so it is
    // not used.)
    // `total` = the rows actually on the panel (Shopify's count is any-word
    // matches capped at 1,000 - 2026-09-16); `totalPlus` = more pages exist.
    const renderCompletions = (box, q, base, total, viewAllUrl, refinements, shopifyQueries, totalPlus) => {
      const rows = [];
      let n = 0;
      rows.push(`<a class="search-completions__row search-completions__row--all" role="option" id="SearchCompletion${n++}" href="${escapeHtml(viewAllUrl)}">${ICON}<span class="search-completions__text">${escapeHtml(q.trim())}</span><span class="search-completions__count">${total ? `${total.toLocaleString()}${totalPlus ? '+' : ''} results` : ''}</span></a>`);
      const qWordsTyped = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
      const lastTyped = qWordsTyped[qWordsTyped.length - 1] || '';
      const baseSet = new Set(base.toLowerCase().split(/\s+/).filter(Boolean));
      const typedSet = new Set(qWordsTyped);
      const isTyped = (w) => typedSet.has(w) || baseSet.has(w) || (lastTyped.length >= 2 && w.startsWith(lastTyped));
      const emphasise = (text) => text.split(/\s+/).map((w) => (isTyped(w.toLowerCase()) ? escapeHtml(w) : `<b>${escapeHtml(w)}</b>`)).join(' ');
      const seen = new Set([q.toLowerCase().trim(), base.toLowerCase()]);
      const seenSets = new Set([[...baseSet].sort().join(' ')]);
      // Each row is a LINK to the results page for that phrase - Aaron: "I'd expect
      // it to go to the search page with those boots on it, not stay in the pop-up."
      // Same destination as pressing the search button with that text typed.
      const resultsUrl = (text) => {
        const u = new URL(viewAllUrl, location.origin);
        applyUnderstanding(u, text);
        return u.toString();
      };
      const push = (text) => {
        const key = [...new Set(text.toLowerCase().split(/\s+/))].sort().join(' ');
        if (seen.has(text.toLowerCase()) || seenSets.has(key)) return;
        seen.add(text.toLowerCase());
        seenSets.add(key);
        rows.push(`<a class="search-completions__row" role="option" id="SearchCompletion${n++}" href="${escapeHtml(resultsUrl(text))}" data-refine="${escapeHtml(text)}">${ICON}<span class="search-completions__text">${emphasise(text)}</span></a>`);
      };
      refinements.forEach((phrase) => push(phrase));   // already whole phrases (maker rows replace a typed maker)
      (shopifyQueries || []).forEach((sq) => {
        const text = (sq.text || '').trim();
        const words = text.toLowerCase().split(/\s+/).filter(Boolean);
        if (!text || !words.length || words.some((w) => w.length < 2)) return;              // "g turnout rug"
        // must still contain every typed word (the last one may be half-typed)
        const containsTyped = qWordsTyped.every((w) => words.includes(w) || (w === lastTyped && words.some((x) => x.startsWith(w))));
        if (!containsTyped) return;
        if (!words.some((w) => !isTyped(w) && w.length >= 3)) return;                       // must add a real word
        push(text);
      });
      box.innerHTML = rows.slice(0, COMPLETIONS_MAX + 1).join('');
    };

    // Tap on a refinement: the box shows the chosen phrase for the moment the page
    // takes to change, and the link goes to the results page. Nothing else - no
    // in-place re-search (Aaron: it should behave like pressing search).
    resultsBox.addEventListener('click', (evt) => {
      const row = evt.target && evt.target.closest && evt.target.closest('[data-refine]');
      if (!row) return;
      searchInput.value = row.getAttribute('data-refine') || '';
      saveRecent(searchInput.value);
      this.classList.add('main-search--loading');
    });

    // Strip: the first STRIP_MAX products as squares. Built from the rows we already
    // have, so it costs no request; the photo is the row's own 144px thumbnail.
    const buildStrip = (rows) => {
      const strip = document.createElement('div');
      strip.className = 'search-strip';
      strip.setAttribute('role', 'group');
      strip.setAttribute('aria-label', 'Top results');
      rows.slice(0, STRIP_MAX).forEach((row, i) => {
        const link = row.querySelector('a.product-link');
        const img = row.querySelector('.search-row__thumb img');
        const title = row.querySelector('.search-row__title');
        const price = row.querySelector('.search-row__price');
        if (!link || !title) return;
        const item = document.createElement('div');
        item.className = 'main-search-result search-strip__item';
        // A square is a keyboard option like a row (the list no longer repeats
        // these ten), and carries its true rank for the click log.
        item.setAttribute('role', 'option');
        item.id = `SearchStrip${i}`;
        item.setAttribute('data-pos', String(i + 1));
        const a = document.createElement('a');
        a.href = link.getAttribute('href');
        a.className = 'search-strip__link';
        const pic = document.createElement('div');
        pic.className = 'search-strip__pic';
        if (img) {
          const i = document.createElement('img');
          // the row thumbnail is served at 144px; the square is up to 132px on desktop,
          // so ask the CDN for 288px - same file, one parameter
          i.src = (img.getAttribute('src') || img.src).replace(/width=\d+/, 'width=288');
          i.alt = '';
          i.width = 104; i.height = 104;
          i.loading = 'lazy';
          i.decoding = 'async';
          pic.appendChild(i);
        }
        const t = document.createElement('span');
        t.className = 'search-strip__title product-block__title';
        t.textContent = title.textContent.trim();
        a.appendChild(pic);
        a.appendChild(t);
        if (price) {
          const p = document.createElement('span');
          p.className = 'search-strip__price';
          p.innerHTML = price.innerHTML;
          a.appendChild(p);
        }
        // The next-day badge, laid over the picture's top-left corner (Aaron:
        // "an overlay over the image, in the corner"). A copy of the row's
        // placeholder; the delivery app's embed fills it like any other card, or
        // leaves it hidden when the item is slower.
        const hint = row.querySelector('.edd-card');
        if (hint) {
          const h = hint.cloneNode(true);
          h.className = 'edd-card edd-card--pill edd-card--badge';
          h.removeAttribute('data-edd-key');
          h.hidden = true;
          pic.appendChild(h);
        }
        item.appendChild(a);
        strip.appendChild(item);
      });
      return strip;
    };

    // Load-more for one query. Separate from the render so Back-restore can re-arm
    // it on the restored list. Pages 2+ are fetched with the SAME options as page 1.
    // The observer watches the bottom sentinel AND a row ~8 from the end: with the
    // panel as its own scroll container, rootMargin cannot reach past the panel's
    // clip, so the sentinel alone only fired at the very bottom.
    // `query` is what is FETCHED (may be the spelling-corrected words); `typed`
    // is what the box holds, which is what tells us the shopper moved on.
    const DROPDOWN_MAX_PAGES = MERGE_PAGES;   // 2026-09-16, Aaron: stop at 3 pages, then "View all"
    const attachLoadMore = (query, startPage, pages, list, sentinel, moreStatus, typed) => {
      typed = typed || query;
      pages = Math.min(pages, DROPDOWN_MAX_PAGES);
      // seen = everything on the panel, strip included (the strip holds rows 1-10
      // and the list carries on from 11, so both must count as shown)
      const seen = new Set(Array.from(resultsBox.querySelectorAll('a[href*="/products/"]'))
        .map((a) => (a.getAttribute('href').split('/products/')[1] || '').split('?')[0]));
      let page = startPage;
      let busy = false;
      this.liveMore = { query: typed, fetchQuery: query, get page() { return page; }, pages };
      if (page >= pages) { sentinel.remove(); moreStatus.textContent = ''; return; }
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      // root: the viewport, NOT the panel. MEASURED: an observer rooted on the
      // fixed-position panel never fired even with the sentinel plainly on screen.
      }, { root: null, rootMargin: '400px 0px' });
      const watchEarly = () => {
        const early = list.children[Math.max(0, list.children.length - 8)];
        if (early) io.observe(early);
      };
      const loadMore = () => {
        if (busy || page >= pages || searchInput.value !== typed) return;
        busy = true; page += 1;
        moreStatus.textContent = 'Loading more…';
        cachedText(buildSearchUrl(query, page), { method: 'get' })
          .then((html) => {
            const d = new DOMParser().parseFromString(html, 'text/html');
            const m = d.querySelector('.search-more');
            if (!m || searchInput.value !== typed) return;      // they moved on
            m.querySelectorAll('.product-block').forEach((row) => {
              const a = row.querySelector('a[href*="/products/"]');
              const h = a ? (a.getAttribute('href').split('/products/')[1] || '').split('?')[0] : '';
              if (!h || seen.has(h)) return;
              seen.add(h);
              row.classList.add('main-search-result');
              row.setAttribute('role', 'option');
              row.id = `SearchResult${list.children.length}`;
              row.setAttribute('data-pos', String(seen.size));                 // rank = products shown so far
              list.appendChild(row);
            });
            if (page >= pages) { io.disconnect(); sentinel.remove(); }
            else watchEarly();
            moreStatus.textContent = '';
          })
          .catch(() => { moreStatus.textContent = ''; })
          .then(() => { busy = false; });
      };
      io.observe(sentinel);
      watchEarly();
    };

    const handleInputChange = () => {
      const valueToSearch = searchInput.value;

      // Only search if search string longer than 2, and it has changed
      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        // Save previous value
        this.oldSearchValue = valueToSearch;

        // Kill outstanding ajax request
        if (searchAbortController !== null) {
          searchAbortController.abort();
          searchAbortController = null;
        }

        // Kill previous search
        clearTimeout(searchTimeoutID);

        // Create URL for full search results
        const form = searchInput.closest('form');
        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));
        formParams.forEach((value, key) => { linkURL.searchParams.set(key, value); });

        // Show loading. 2026-09-09: results already on screen STAY there and CSS dims
        // them (.main-search--loading), so rows swap under a live panel. The spinner
        // is only for the first search, when there is genuinely nothing worth keeping.
        this.classList.add('main-search--loading');
        if (this.querySelector('.main-search-result') === null) {
          this.classList.remove('main-search--has-results', 'main-search--no-results');
          if (!this.querySelector('.main-search__results-spinner')) {
            resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
          }
        }

        // Do next search (in X milliseconds)
        searchTimeoutID = setTimeout(() => {
          searchAbortController = new AbortController();
          const query = valueToSearch;
          const pageUrls = Array.from({ length: MERGE_PAGES }, (_, i) => buildSearchUrl(query, i + 1));

          // Shopify's phrase suggestions, in parallel. Small and fast; when it lands
          // the text rows are redrawn with its phrases first, and its top phrase is
          // prefetched so finishing the word is a memory hit.
          let shopifyQueries = [];
          const suggestP = fetch(buildSuggestUrl(query), { signal: searchAbortController.signal })
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => { shopifyQueries = (j && j.resources && j.resources.results && j.resources.results.queries) || []; })
            .catch(() => { shopifyQueries = []; });

          // page 1 must arrive; a failed OR SLOW page 2 or 3 just means fewer
          // candidates this time (2.5s: a cold render is 0.8-1.1s, so a page that
          // is still not here at 2.5s is stuck, and page 1 is already in hand)
          const signal = searchAbortController.signal;
          const EXTRA_PAGE_WAIT = 2500;
          const withPatience = (p, i) => (i === 0 ? p : Promise.race([p, new Promise((res) => setTimeout(() => res(''), EXTRA_PAGE_WAIT))]));
          Promise.all(pageUrls.map((u, i) => withPatience(cachedText(u, { method: 'get', signal })
            .catch((e) => { if (i === 0 || (e && e.name === 'AbortError')) throw e; return ''; }), i)))
            .then((responses) => {
              if (searchInput.value !== query) return;                    // they moved on (shared request)
              const parse = (rs) => {
                const docs = rs.map((r) => (r ? new DOMParser().parseFromString(r, 'text/html') : null));
                const m = docs[0] ? docs[0].querySelector('.search-more') : null;
                return {
                  rows: m ? mergeRows(docs) : [],
                  pages: m ? (parseInt(m.getAttribute('data-pages') || '0', 10) || 0) : 0,
                  total: m ? (parseInt(m.getAttribute('data-total') || '0', 10) || 0) : 0,
                };
              };
              const first = parse(responses);
              // SPELLING (2026-09-15): "horse riiding boot" filled the panel with
              // hoof boots - Shopify's tolerance found riding boots, our word tier
              // could not see that "riiding" was "riding". search-spell.js finds the
              // correction in the titles that came back; then search once more with
              // the right words and say so. The typed words stay in the box.
              const titles1 = first.rows.map((r) => (r.querySelector('.search-row__title') || {}).textContent || '');
              let fixed = null;
              try {
                if (first.rows.length && window.jhrSpell) {
                  fixed = window.jhrSpell.correct(query, titles1);
                  if (fixed && fixed.toLowerCase() === query.toLowerCase()) fixed = null;
                }
              } catch (e) { fixed = null; }
              // search once more with better words; keep the first rows if that finds nothing
              const searchAgain = (better) => {
                const fixedUrls = Array.from({ length: MERGE_PAGES }, (_, i) => buildSearchUrl(better, i + 1));
                Promise.all(fixedUrls.map((u, i) => withPatience(cachedText(u, { method: 'get', signal })
                  .catch((e) => { if (i === 0 || (e && e.name === 'AbortError')) throw e; return ''; }), i)))
                  .then((rs2) => {
                    if (searchInput.value !== query) return;
                    const second = parse(rs2);
                    if (second.rows.length) finish(second.rows, second.pages, second.total, better);
                    else finish(first.rows, first.pages, first.total, null);
                  })
                  .catch((error) => {
                    if (error && error.name === 'AbortError') return;
                    finish(first.rows, first.pages, first.total, null);
                  });
              };
              if (fixed) { searchAgain(fixed); return; }
              // RESCUE (2026-09-29, Aaron): nothing found, or nothing carrying a typed word - a size written the
              // shop's way ("6ft3" -> 6'3), then a brand we sell ("dobson and ho" -> Dodson & Horrell). Shown with
              // the same "Showing results for" line as a spelling fix (search-spell.js).
              let rescuing = false;
              try {
                if (window.jhrSpell && window.jhrSpell.rescue && (!first.rows.length || window.jhrSpell.unrelated(query, titles1))) {
                  rescuing = true;
                  window.jhrSpell.rescue(query).then((better) => {
                    if (searchInput.value !== query) return;
                    if (better && better.toLowerCase() !== query.toLowerCase()) searchAgain(better);
                    else finish(first.rows, first.pages, first.total, null);
                  }).catch(() => { if (searchInput.value === query) finish(first.rows, first.pages, first.total, null); });
                }
              } catch (e) { rescuing = false; }
              if (rescuing) return;
              finish(first.rows, first.pages, first.total, null);
            })
            .catch((error) => {
              if (error && error.name === 'AbortError') return;
              console.warn(error); // eslint-disable-line no-console
            });

          // Draw the panel. `fixed` = the corrected words the rows are for, or
          // null when they are for the words as typed.
          const finish = (rows, pages, total, fixed) => {
              const shownFor = fixed || query;
              if (fixed) linkURL.searchParams.set('q', fixed);

              // reset previous contents
              searchAbortController = null;
              this.classList.remove('main-search--has-results', 'main-search--no-results', 'main-search--trending');
              resultsBox.innerHTML = '';

              // Stage 5 search logging. Runs on every keystroke, but does NOT log on
              // every keystroke: search-logger.js restarts a separate ~1.2s timer and
              // records only the query the shopper actually stopped on. Wrapped so a
              // logging fault can never break the dropdown.
              try {
                if (window.jhrSearchLog) window.jhrSearchLog.onResults(query, rows.length, total, fixed);
              } catch (e) { /* deliberately swallowed */ }

              this.classList.remove('main-search--loading');

              if (!rows.length) {
                // No results - show nothing
                this.classList.add('main-search--no-results');
                const emptyMessage = document.createElement('div');
                emptyMessage.className = 'main-search__empty-message';
                // 2 Oct 2026 (Aaron): nothing found -> "Ask us on WhatsApp" with the search already written in the
                // message (snippets/jhr-search-help.liquid). Short words while still typing keep the old line.
                if (window.jhrSearchHelp && query.trim().length >= 3) {
                  emptyMessage.appendChild(window.jhrSearchHelp.el(query, 'dropdown'));
                } else {
                  emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                }
                resultsBox.appendChild(emptyMessage);
                searchInput.setAttribute('aria-expanded', false);
                return;
              }

              const status = document.querySelector('#HeaderSearchResultsStatus');
              const shownCount = rows.length;
              const morePages = pages > MERGE_PAGES;
              if (status) status.textContent = `${shownCount}${morePages ? '+' : ''} results for ${shownFor}`;
              this.classList.add('main-search--has-results');
              if (this.noteResultsFor) this.noteResultsFor(query);   // keyboard may rest now

              // 1. text rows first (a "Showing results for" line first when
              // corrected), then the products as ONE list from #1. 2026-09-16,
              // Aaron: the sideways strip went - "you instinctively scroll down".
              const completions = document.createElement('div');
              completions.className = 'search-completions';
              completions.setAttribute('role', 'group');
              completions.setAttribute('aria-label', 'Suggestions');
              const base = completeLastWord(shownFor, rows);
              const refinements = buildRefinements(base, rows);
              const draw = () => {
                renderCompletions(completions, shownFor, base, shownCount, linkURL.toString(), refinements, fixed ? [] : shopifyQueries, morePages);
                if (fixed) {
                  const note = document.createElement('div');
                  note.className = 'search-spell-note search-spell-note--panel';
                  note.innerHTML = 'Showing results for <b></b> instead of "<span></span>"';
                  note.querySelector('b').textContent = fixed;
                  note.querySelector('span').textContent = query;
                  completions.insertAdjacentElement('afterbegin', note);
                }
              };
              draw();
              resultsBox.appendChild(completions);
              suggestP.then(() => {
                if (searchInput.value !== query || !completions.isConnected || fixed) return;
                draw();
                if (shopifyQueries.length && window.jhrSearchPrefetch) window.jhrSearchPrefetch(shopifyQueries[0].text);
              });

              // 2. the list, from the top result
              const resultsProducts = document.createElement('div');
              resultsProducts.className = 'main-search__results__products collection-listing';
              const list = document.createElement('div');
              list.className = 'search-list';
              rows.forEach((block, index) => {
                block.setAttribute('role', 'option');
                block.id = `SearchResult${index}`;
                block.setAttribute('data-pos', String(index + 1));   // true rank
                block.classList.add('main-search-result');
                list.appendChild(block);
              });
              resultsProducts.appendChild(list);
              resultsBox.appendChild(resultsProducts);

              const allLink = document.createElement('a');
              allLink.className = 'main-search__results-all-link btn btn--secondary';
              allLink.href = linkURL;
              allLink.innerHTML = theme.strings.generalSearchViewAll;
              resultsBox.appendChild(allLink);
              // 2 Oct 2026 (Aaron): Shopify always offers its "closest" products now, so "nothing found" is rare -
              // keep a way to ask at the foot of every panel (snippets/jhr-search-help.liquid).
              try { if (window.jhrSearchHelp && window.jhrSearchHelp.slim && query.trim().length >= 3) resultsBox.appendChild(window.jhrSearchHelp.slim(query, 'dropdown_footer')); } catch (e) { /* never in the way */ }
              searchInput.setAttribute('aria-expanded', true);
              this.activeResultIndex = -1;

              // Pages 1-3 are already merged above; the dropdown stops there
              // (2026-09-16, Aaron: "a maximum of 3 pages") and "View all results"
              // takes over. attachLoadMore caps itself at DROPDOWN_MAX_PAGES.
              const sentinel = document.createElement('div');
              sentinel.className = 'main-search__more-sentinel';
              const moreStatus = document.createElement('div');
              moreStatus.className = 'main-search__more-status';
              moreStatus.setAttribute('role', 'status');
              allLink.insertAdjacentElement('beforebegin', sentinel);
              sentinel.insertAdjacentElement('afterend', moreStatus);
              attachLoadMore(shownFor, MERGE_PAGES, pages, list, sentinel, moreStatus, query);
          };
        }, searchTimeoutThrottle);
      } else if (!valueToSearch.length) {
        // Abandon current search
        this.oldSearchValue = valueToSearch;
        if (searchAbortController !== null) {
          searchAbortController.abort();
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);

        // Clear results
        this.classList.remove('main-search--has-results', 'main-search--loading');
        resultsBox.innerHTML = '';
        searchInput.setAttribute('aria-expanded', false);
        showTrending();
      }
    };

    // 2026-09-11: BACK RETURNS YOU TO THE SEARCH. Tapping a result remembers the
    // query, the rendered results and the scroll position; arriving via Back puts
    // all three straight back with no network request. Only ever on Back - a
    // normal visit must never pop the search open. Expires after 15 minutes.
    const RESTORE_KEY = 'jhr_search_restore';
    this.addEventListener('click', (evt) => {
      const link = evt.target && evt.target.closest && evt.target.closest('.main-search-result a');
      if (!link || !searchInput.value.length) return;
      saveRecent(searchInput.value);
      try {
        const href = link.getAttribute('href') || '';
        const st = resultsBox.querySelector('.main-search__more-status');
        if (st) st.textContent = '';                         // never restore a frozen "Loading more…"
        const more = this.liveMore && this.liveMore.query === searchInput.value ? this.liveMore : null;
        sessionStorage.setItem(RESTORE_KEY, JSON.stringify({
          q: searchInput.value,
          html: resultsBox.innerHTML,
          scroll: this.scrollTop,
          handle: (href.split('/products/')[1] || '').split('?')[0].split('#')[0],
          from: location.pathname + location.search,         // only the Back TO this page restores
          page: more ? more.page : null,
          pages: more ? more.pages : null,
          fetchQ: more ? more.fetchQuery : null,
          t: Date.now()
        }));
      } catch (e) { /* private mode or quota - Back just works the old way */ }
    }, true);

    const restoreFromBack = () => {
      let state = null;
      try {
        const nav = performance.getEntriesByType('navigation')[0];
        if (!nav || nav.type !== 'back_forward') return;
        state = JSON.parse(sessionStorage.getItem(RESTORE_KEY) || 'null');
        // A Back to some OTHER page (product -> related product -> Back) must not
        // pop the search open; the state waits for the Back to the page it was saved on.
        if (!state || state.from !== location.pathname + location.search) return;
        sessionStorage.removeItem(RESTORE_KEY);
      } catch (e) { return; }
      if (!state || !state.q || !state.html || Date.now() - state.t > 15 * 60 * 1000) return;

      const opener = document.querySelector('.show-search-link');
      if (opener) opener.click(); else document.body.classList.add('show-search');

      searchInput.value = state.q;
      if (this.syncTyped) this.syncTyped();
      this.oldSearchValue = state.q;                       // so the next keyup is not a refetch
      resultsBox.innerHTML = state.html;
      this.classList.remove('main-search--trending', 'main-search--loading', 'main-search--no-results');
      this.classList.add('main-search--has-results');
      searchInput.setAttribute('aria-expanded', true);
      // re-arm "keep loading" on the restored list
      const rList = resultsBox.querySelector('.search-list');
      const rSentinel = resultsBox.querySelector('.main-search__more-sentinel');
      const rStatus = resultsBox.querySelector('.main-search__more-status');
      if (rList && rSentinel && rStatus && state.page && state.pages) {
        attachLoadMore(state.fetchQ || state.q, state.page, state.pages, rList, rSentinel, rStatus, state.q);
      }
      // Put the tapped product back in view. A pixel offset drifts on a real phone
      // (the panel is still animating open, images settle at slightly different
      // heights), so scroll the ROW itself into the middle of the panel; the saved
      // offset is only the fallback if that row is somehow gone.
      const row = state.handle
        ? Array.from(resultsBox.querySelectorAll('.main-search-result a[href*="/products/"]'))
            .map((a) => a.closest('.main-search-result'))
            .find((r) => r && r.querySelector('a[href*="/products/' + state.handle + '"]'))
        : null;
      let tries = 0;
      const settle = () => {
        // a strip square scrolls sideways into view as well as up
        if (row) row.scrollIntoView({ block: 'center', inline: 'center', behavior: 'instant' });
        else this.scrollTop = state.scroll || 0;
        if (++tries < 9) setTimeout(settle, 80);   // repeat through the open animation
      };
      setTimeout(settle, 40);
      // The theme focuses the input ~500ms after opening, which scrolls the panel
      // back to the top. Re-centre once more right after that focus lands.
      searchInput.addEventListener('focus', () => { tries = 0; requestAnimationFrame(settle); }, { once: true });
    };
    // main.js binds the opener on DOMContentLoaded; run after it whichever order we load in
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setTimeout(restoreFromBack, 0));
    else setTimeout(restoreFromBack, 0);

    // 2026-09-15 (Aaron, on his phone): "when I tap the search icon I then have to
    // tap the box for the keyboard to come up." The theme focuses the box 500ms
    // after the tap (main.js, PageHeader); iPhones only open the keyboard for a
    // focus that happens DURING the tap. Capture phase, so this runs before the
    // theme's handler: open the panel and focus the box in the tap itself. The
    // theme's later focus lands on an already-focused box and changes nothing.
    document.addEventListener('click', (evt) => {
      const link = evt.target && evt.target.closest ? evt.target.closest('.show-search-link') : null;
      if (!link || !this.contains(searchInput)) return;
      document.body.classList.add('show-search');
      // The panel fades in over 350ms and its `visibility` transition reads as
      // hidden for the first frame - and a hidden box cannot take focus. The
      // box itself has no transition, so making IT visible takes effect at
      // once (a visible child of a hidden parent is focusable); the focus and
      // the keyboard land in this tap. Handed back to the stylesheet after the fade.
      searchInput.style.visibility = 'visible';
      try { searchInput.focus({ preventScroll: true }); } catch (e) { searchInput.focus(); }
      setTimeout(() => { searchInput.style.visibility = ''; }, 400);
    }, true);

    searchInput.addEventListener('focus', () => { fitToViewport(); if (!searchInput.value.length) showTrending(); });
    // 'input', not 'keyup': iPhone QuickType, paste and dictation change the value
    // without a key going up. 'change' stays as the fallback for anything else.
    searchInput.addEventListener('input', handleInputChange);
    searchInput.addEventListener('change', handleInputChange);
  }
};

window.customElements.define('main-search', MainSearch);
